// Envío de fotos por QR: el generador de hiloramas (tablet/PC) muestra un QR
// con un token; el teléfono abre /subir/<token> sin iniciar sesión, sube la
// foto a Storage y anota la URL en Firestore. El generador escucha ese
// documento y carga la foto como si se hubiera elegido en el propio equipo.
//
// El token es la única "credencial": es aleatorio, largo y solo vive mientras
// el generador está abierto, así que las reglas de Firebase permiten escribir
// en fotosQR/<token> sin autenticación (ver storage.rules y firestore.rules).
//
// Para que el generador pueda descargar la foto el bucket necesita política
// CORS (ver cors.json); sin ella fetch() falla con "Failed to fetch".
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';

export const COLECCION_FOTOS_QR = 'fotosQR';
export const RUTA_SUBIR = '/subir/';

// Lado mayor al que se reduce la foto antes de subirla. El generador dibuja
// sobre un lienzo de 720 px y analiza como mucho a 1024, así que subir el
// original de 12 MP solo alarga la espera en la red del teléfono.
const LADO_MAX = 1280;
const CALIDAD = 0.82;

export const generarToken = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
};

export const esTokenValido = (t) => typeof t === 'string' && /^[a-f0-9]{32}$/.test(t);

export const urlSubida = (token) => `${window.location.origin}${RUTA_SUBIR}${token}`;

export const tokenDesdeRuta = (pathname = window.location.pathname) => {
  if (!pathname.startsWith(RUTA_SUBIR)) return null;
  const t = pathname.slice(RUTA_SUBIR.length).replace(/\/+$/, '');
  return esTokenValido(t) ? t : null;
};

// Decodifica y reescala en un paso. createImageBitmap descomprime fuera del
// hilo principal, que en un teléfono es bastante más rápido que <img> + canvas;
// si no está disponible se usa la ruta clásica.
const decodificarEscalada = async (file) => {
  const medida = (w, h) => {
    const escala = Math.min(1, LADO_MAX / Math.max(w, h));
    return { w: Math.max(1, Math.round(w * escala)), h: Math.max(1, Math.round(h * escala)), escala };
  };

  if (typeof createImageBitmap === 'function') {
    try {
      const sonda = await createImageBitmap(file);
      const { w, h, escala } = medida(sonda.width, sonda.height);
      if (escala === 1) return sonda;
      const bmp = await createImageBitmap(sonda, { resizeWidth: w, resizeHeight: h, resizeQuality: 'high' });
      sonda.close?.();
      return bmp;
    } catch {
      // Formato que createImageBitmap no entiende: seguimos por la ruta clásica.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('Imagen no legible'));
      i.src = url;
    });
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Reduce la foto a JPEG. Si algo falla (HEIC sin soporte, formato raro…) se
// sube el archivo original antes que dejar al usuario sin enviar nada.
const comprimirImagen = async (file) => {
  try {
    const fuente = await decodificarEscalada(file);
    const ancho = fuente.width, alto = fuente.height;
    const escala = Math.min(1, LADO_MAX / Math.max(ancho, alto));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(ancho * escala));
    canvas.height = Math.max(1, Math.round(alto * escala));
    canvas.getContext('2d').drawImage(fuente, 0, 0, canvas.width, canvas.height);
    fuente.close?.();
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', CALIDAD));
    // Si comprimir no ayuda (foto ya pequeña), se manda la original.
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
};

// Sube la foto y publica la URL para que el generador la recoja.
// onProgreso recibe 0..100 mientras viajan los bytes.
export const subirFotoQR = async (token, file, onProgreso) => {
  if (!esTokenValido(token)) throw new Error('Token inválido');
  const blob = await comprimirImagen(file);
  const nombre = `${Date.now()}.jpg`;
  const ref = storageRef(storage, `${COLECCION_FOTOS_QR}/${token}/${nombre}`);

  const tarea = uploadBytesResumable(ref, blob, { contentType: blob.type || 'image/jpeg' });
  await new Promise((resolve, reject) => {
    tarea.on('state_changed',
      (snap) => {
        if (!snap.totalBytes) return;
        onProgreso?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
      },
      reject,
      resolve);
  });

  const url = await getDownloadURL(ref);
  await setDoc(doc(db, COLECCION_FOTOS_QR, token), {
    url,
    ruta: ref.fullPath,
    nombre: file.name || nombre,
    subidoEn: serverTimestamp(),
    subidoMs: Date.now(),
  });
  return url;
};

// Escucha el documento del token. Llama a onFoto({ url, nombre }) cada vez que
// llega una foto nueva (ignora la que ya estaba al suscribirse, por si el
// token se reutiliza). Devuelve la función para cancelar.
export const escucharFotoQR = (token, onFoto, onError) => {
  let ultimaMs = null;
  let primera = true;
  return onSnapshot(doc(db, COLECCION_FOTOS_QR, token), (snap) => {
    const data = snap.exists() ? snap.data() : null;
    const ms = data?.subidoMs ?? null;
    if (primera) { primera = false; ultimaMs = ms; return; }
    if (!data?.url || ms === ultimaMs) return;
    ultimaMs = ms;
    onFoto({ url: data.url, nombre: data.nombre || 'foto.jpg' });
  }, (err) => { console.error('Error escuchando fotosQR:', err); onError?.(err); });
};

// Descarga la foto publicada y la devuelve como File, listo para el mismo
// flujo que una foto elegida localmente. Requiere la política CORS del bucket.
export const descargarFotoQR = async ({ url, nombre }) => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`No se pudo descargar la foto (${res.status})`);
  const blob = await res.blob();
  return new File([blob], nombre, { type: blob.type || 'image/jpeg' });
};
